import { Injectable } from '@angular/core';
import { Observable, of, delay, tap } from 'rxjs';
import { CandidateService } from './candidate.service';
import { Candidate, CandidateSearchParam } from '../../interfaces/candidate/candidate.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class CandidateMockService extends CandidateService {

    private mockCandidates: Candidate[] = [
        { uniqueId: '1', fullName: 'Alice Johnson', email: 'alice@example.com', phone: '9876543210', department: 'Computer Science', graduationYear: '2024', status: 'ACTIVE', createdAt: '2023-01-10', batchName: '2024 Alpha' },
        { uniqueId: '2', fullName: 'Bob Smith', email: 'bob@example.com', phone: '9876543211', department: 'Information Technology', graduationYear: '2024', status: 'PLACED', createdAt: '2023-01-15', batchName: '2024 Beta' },
        { uniqueId: '3', fullName: 'Charlie Brown', email: 'charlie@example.com', phone: '9876543212', department: 'Mechanical Engineering', graduationYear: '2024', status: 'ACTIVE', createdAt: '2023-02-01', batchName: '2024 Delta' },
        { uniqueId: '4', fullName: 'David Wilson', email: 'david@example.com', phone: '9876543213', department: 'Electronics', graduationYear: '2023', status: 'PLACED', createdAt: '2022-11-20', batchName: '2023 Gamma' },
        { uniqueId: '5', fullName: 'Eve Adams', email: 'eve@example.com', phone: '9876543214', department: 'Computer Science', graduationYear: '2024', status: 'PENDING', createdAt: '2023-03-05', batchName: '2024 Alpha' },
    ];

    override findAll(searchParam: CandidateSearchParam): Observable<PageResponse<Candidate>> {
        let filtered = this.mockCandidates;
        if (searchParam.searchTerm) {
            const term = searchParam.searchTerm.toLowerCase();
            filtered = filtered.filter(c => c.fullName.toLowerCase().includes(term) || c.email.toLowerCase().includes(term));
        }

        return this.wrapRequest(
            of({
                content: filtered,
                page: {
                    number: searchParam.page,
                    size: searchParam.size,
                    totalElements: filtered.length,
                    totalPages: Math.ceil(filtered.length / searchParam.size)
                }
            }).pipe(delay(800))
        );
    }

    override find(id: string): Observable<Candidate> {
        const candidate = this.mockCandidates.find(c => c.uniqueId === id);
        return this.wrapRequest(of(candidate!).pipe(delay(500)));
    }

    override create(data: Partial<Candidate>): Observable<Candidate> {
        const newCandidate = { ...data, uniqueId: Math.random().toString(36).substr(2, 9), createdAt: new Date().toISOString() } as Candidate;
        this.mockCandidates.push(newCandidate);
        return this.wrapRequest(of(newCandidate).pipe(delay(1000)).pipe(tap(() => this.toast.success('Student added successfully'))));
    }

    override update(id: string, data: Partial<Candidate>): Observable<Candidate> {
        const index = this.mockCandidates.findIndex(c => c.uniqueId === id);
        if (index !== -1) {
            this.mockCandidates[index] = { ...this.mockCandidates[index], ...data };
        }
        return this.wrapRequest(of(this.mockCandidates[index]).pipe(delay(1000)).pipe(tap(() => this.toast.success('Student updated successfully'))));
    }

    override delete(id: string): Observable<void> {
        this.mockCandidates = this.mockCandidates.filter(c => c.uniqueId !== id);
        return this.wrapRequest(of(undefined).pipe(delay(800)).pipe(tap(() => this.toast.success('Student deleted successfully'))));
    }
}
